/**
 * 埋め込み認証用のサーバー基準時計。PC の日時設定を期限の基準にしない。
 * 同じ仕様を各 MiniApp に配置する。認証・失効の最終判定はサーバーで行う。
 */
export type EmbeddedAuthTimeSource = {
  customToken: string;
  serverTime?: string;
};

export function getEmbeddedAuthServerTime(source: EmbeddedAuthTimeSource): number | null {
  if (source.serverTime !== undefined) {
    if (typeof source.serverTime !== "string") return null;
    const value = Date.parse(source.serverTime);
    return Number.isFinite(value) ? value : null;
  }
  // serverTime を省略する要求ID対応ホスト用。iat はサーバーの時刻（秒）。
  // ここでは時計の基準だけを読み、署名検証は Firebase Auth に委ねる。
  try {
    const payload = source.customToken.split(".")[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const decoded: unknown = JSON.parse(globalThis.atob(base64));
    if (typeof decoded !== "object" || decoded === null) return null;
    const iat = (decoded as Record<string, unknown>).iat;
    return typeof iat === "number" && Number.isFinite(iat * 1000) ? iat * 1000 : null;
  } catch {
    return null;
  }
}

type AuthResponseId = { requestId?: string; messageId?: string };
type ClockSample = { monotonic: number; wall: number };
type ServerClock = { serverTime: number; time: number; observedAt: number };

export function createEmbeddedAuthClock(
  monotonicNow: () => number = () => performance.now(),
  wallNow: () => number = () => Date.now(),
) {
  const sample = (): ClockSample => ({ monotonic: monotonicNow(), wall: wallNow() });
  const read = (value: ServerClock): number => value.time + Math.max(0, monotonicNow() - value.observedAt);
  let confirmed: ServerClock = { serverTime: Number.NEGATIVE_INFINITY, time: 0, observedAt: monotonicNow() };
  let pending: (ServerClock & { requestId: string; issuedAt: number }) | null = null;
  const requests = new Map<string, ClockSample & { sequence: number }>();
  let requestSequence = 0;
  let latestResponseSequence = 0;
  let resumeTimeout: ReturnType<typeof setTimeout> | undefined;
  let onUnavailable: (() => void) | undefined;
  let onDiscontinuity: (() => void) | undefined;
  let stopWatching: (() => void) | undefined;
  let lastSample = sample();
  const now = (): number => read(pending ?? confirmed);
  const clearResumeTimeout = () => {
    clearTimeout(resumeTimeout);
    resumeTimeout = undefined;
  };
  const invalidatePending = () => {
    requests.clear();
    pending = null;
  };
  const armCompletionTimeout = () => {
    if (!onUnavailable || resumeTimeout !== undefined) return;
    resumeTimeout = setTimeout(() => {
      resumeTimeout = undefined;
      invalidatePending();
      onUnavailable?.();
    }, 5000);
  };
  const beginRecovery = (unavailable: () => void): void => {
    invalidatePending();
    // 復帰イベントが重なっても、進行中の復旧の期限は延長しない。
    onUnavailable = unavailable;
    lastSample = sample();
    armCompletionTimeout();
  };
  // 日付の正しさは使わない。2種類の経過時間が食い違ったら再取得するだけ。
  const hasDiscontinuity = (before: ClockSample, after: ClockSample): boolean =>
    Math.abs((after.wall - before.wall) - (after.monotonic - before.monotonic)) > 1000;
  const checkDiscontinuity = (): boolean => {
    const current = sample();
    const changed = hasDiscontinuity(lastSample, current);
    lastSample = current;
    if (changed) {
      invalidatePending();
      onDiscontinuity?.();
    }
    return changed;
  };
  return {
    now,
    // 認証世代を破棄する経路でも、次の認証完了まで監視する。
    beginRecovery,
    createRequest(requestId: string = crypto.randomUUID()): { requestId: string } {
      if (requests.size >= 16) requests.clear();
      requests.set(requestId, { ...sample(), sequence: ++requestSequence });
      // 一度復旧へ入った後は、再試行も受信だけでなく認証完了まで監視する。
      armCompletionTimeout();
      return { requestId };
    },
    synchronize(source: EmbeddedAuthTimeSource & AuthResponseId & { expiresAt?: string; issuedAt?: number | string }): boolean {
      const requestId = source.requestId ?? source.messageId;
      if (!requestId) return false;
      const requestedAt = requests.get(requestId);
      if (!requestedAt) return false;
      // 未確認のサーバー日時ではなく、子画面の要求順で遅延応答を拒否する。
      if (requestedAt.sequence < latestResponseSequence) return false;
      // 復帰イベントや監視タイマーより先に届いた、休止中の古い応答も拒否する。
      if (checkDiscontinuity()) return false;
      requests.delete(requestId);
      const receivedAt = sample();
      if (hasDiscontinuity(requestedAt, receivedAt)) {
        invalidatePending();
        onDiscontinuity?.();
        return false;
      }
      const nextServerTime = getEmbeddedAuthServerTime(source);
      if (nextServerTime === null || nextServerTime < confirmed.serverTime) return false;
      const issuedAt = typeof source.issuedAt === "number"
        ? source.issuedAt
        : typeof source.issuedAt === "string" ? Date.parse(source.issuedAt) : nextServerTime;
      // 認証中も新しい発行を優先する。失敗時はreject/timeoutでこの仮値を解除する。
      if (pending && (nextServerTime < pending.serverTime || issuedAt < pending.issuedAt)) return false;
      const candidateTime = nextServerTime + Math.max(0, receivedAt.monotonic - requestedAt.monotonic);
      const time = Math.max(
        candidateTime,
        nextServerTime === confirmed.serverTime ? read(confirmed) : Number.NEGATIVE_INFINITY,
        pending?.serverTime === nextServerTime ? read(pending) : Number.NEGATIVE_INFINITY,
      );
      // 未検証の応答は仮の時計。失敗しても確定済み時刻の上限を汚さない。
      latestResponseSequence = requestedAt.sequence;
      pending = { requestId, issuedAt, serverTime: nextServerTime, time, observedAt: receivedAt.monotonic };
      return true;
    },
    confirm(source: AuthResponseId & { expiresAt?: string }): boolean {
      if (checkDiscontinuity()) return false;
      const requestId = source.requestId ?? source.messageId;
      if (!requestId || requestId !== pending?.requestId) return false;
      if (source.expiresAt !== undefined && Date.parse(source.expiresAt) <= now()) {
        invalidatePending();
        onDiscontinuity?.();
        return false;
      }
      confirmed = pending;
      pending = null;
      // 復旧成功でこの試行を閉じ、残存応答を監視なしで適用しない。
      requests.clear();
      clearResumeTimeout();
      onUnavailable = undefined;
      return true;
    },
    reject(source: AuthResponseId): void {
      if ((source.requestId ?? source.messageId) === pending?.requestId) pending = null;
    },
    resume(requestToken: () => void, unavailable: () => void): void {
      beginRecovery(unavailable);
      requestToken();
    },
    watchForDiscontinuity(onResume: () => void): () => void {
      stopWatching?.();
      onDiscontinuity = onResume;
      lastSample = sample();
      const interval = setInterval(checkDiscontinuity, 1000);
      const stop = () => {
        clearInterval(interval);
        onDiscontinuity = undefined;
        stopWatching = undefined;
      };
      stopWatching = stop;
      return stop;
    },
    dispose(): void {
      stopWatching?.();
      invalidatePending();
      clearResumeTimeout();
      onUnavailable = undefined;
    },
  };
}

/** スリープ・ページ復帰後はサーバーから新しい時刻と認証情報を取得する。 */
export function subscribeToEmbeddedAuthResume(
  requestToken: () => void,
  clock?: ReturnType<typeof createEmbeddedAuthClock>,
): () => void {
  const onResume = () => {
    if (document.visibilityState === "visible") requestToken();
  };
  const stopWatching = clock?.watchForDiscontinuity(requestToken);
  window.addEventListener("pageshow", onResume);
  window.addEventListener("online", onResume);
  window.addEventListener("focus", onResume);
  document.addEventListener("visibilitychange", onResume);
  return () => {
    stopWatching?.();
    window.removeEventListener("pageshow", onResume);
    window.removeEventListener("online", onResume);
    window.removeEventListener("focus", onResume);
    document.removeEventListener("visibilitychange", onResume);
  };
}
